import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { db } from "services/firebase";
import { collection, getDocs, query, where, writeBatch, onSnapshot } from "firebase/firestore";
import { useAuth } from "./AuthContext";

interface PessoaDoDia {
  dataHoraPessoas: string;
  fotoUrl: string;
}

interface Relatorio {
  id: string;
  Data: string;
  contador: number;
  dispositivoId: string;
  DiaSemana: string;
  expediente: string; 
  pessoasDoDia?: PessoaDoDia[];
  pesquisa?: {
    titulo: string;
    avaliacoes: Record<string, number>;
  };
  sobraLimpa?: Record<string, {
    descricao: string;
    status: string;
    peso: number;
    percentual: string;
    justificativa: string;
  }>;
}

interface RelatorioContextType {
  relatorios: Relatorio[];
  carregarRelatorios: () => Promise<void>;
  removerRelatoriosPorDispositivo: (deviceId: string) => Promise<void>;
}

const RelatorioContext = createContext<RelatorioContextType>({
  relatorios: [],
  carregarRelatorios: async () => {},
  removerRelatoriosPorDispositivo: async () => {},
});

export const useRelatorios = () => useContext(RelatorioContext);

export const RelatorioProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [relatorios, setRelatorios] = useState<Relatorio[]>([]);

  const getCollectionRef = useCallback(() => {
    if (!user?.uid) return null;
    return collection(db, `clientes/${user.uid}/relatorios`);
  }, [user?.uid]); 

  const carregarRelatorios = async () => {
    const relatoriosCollectionRef = getCollectionRef();
    if (!relatoriosCollectionRef) {
      setRelatorios([]);
      return;
    }

    try {
      const snapshot = await getDocs(query(relatoriosCollectionRef));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Relatorio[];
      setRelatorios(data);
    } catch (error) {
      console.error("Erro ao carregar relatórios manualmente:", error);
    }
  };

  useEffect(() => {
    if (!user?.uid) {
      setRelatorios([]);
      return;
    }

    const relatoriosCollectionRef = getCollectionRef();
    if (!relatoriosCollectionRef) return;

    const unsubscribe = onSnapshot(query(relatoriosCollectionRef), (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Relatorio[];
      setRelatorios(data);
    }, (error) => {
      console.error("Erro no listener de relatórios:", error);
    });

    return () => unsubscribe();
  }, [user, getCollectionRef]);

  const removerRelatoriosPorDispositivo = async (deviceId: string) => {
    const relatoriosCollectionRef = getCollectionRef();
    if (!relatoriosCollectionRef) return;

    try {
      const q = query(relatoriosCollectionRef, where("dispositivoId", "==", deviceId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) return;

      const batch = writeBatch(db);
      snapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      
      await batch.commit();
    } catch (error) {
      console.error("Erro ao remover relatórios por dispositivo:", error);
      throw error;
    }
  };

  return (
    <RelatorioContext.Provider value={{ relatorios, carregarRelatorios, removerRelatoriosPorDispositivo }}>
      {children}
    </RelatorioContext.Provider>
  );
};
